/**
 * FlagIcon — Minimal SVG flag set for Alchamis contact surfaces.
 * Designed for small inline use (16–24px). Stroked with white/10 for luxe edge.
 *
 * Supported: US, UK (Union Jack), DK (Denmark), MA (Morocco).
 */

function Frame({ children, width, height }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 60 40"
      width={width}
      height={height}
      className="shrink-0 rounded-[1px] ring-1 ring-white/10"
      aria-hidden="true"
      style={{ display: "block" }}
    >
      {children}
    </svg>
  );
}

function USFlag({ size = 16 }) {
  // Simplified US — 13 stripes + canton with subtle stars
  return (
    <Frame width={size * 1.5} height={size}>
      <rect width="60" height="40" fill="#B22234" />
      {[1, 3, 5, 7, 9, 11].map((i) => (
        <rect key={i} y={(40 / 13) * i} width="60" height={40 / 13} fill="#FFFFFF" />
      ))}
      <rect width="24" height={(40 / 13) * 7} fill="#3C3B6E" />
      {/* tiny star cluster */}
      {[...Array(4)].map((_, r) =>
        [...Array(5)].map((_, c) => (
          <circle
            key={`${r}-${c}`}
            cx={2.5 + c * 4.5}
            cy={2.5 + r * 4.5}
            r="0.7"
            fill="#FFFFFF"
          />
        )),
      )}
    </Frame>
  );
}

function UKFlag({ size = 16 }) {
  return (
    <Frame width={size * 1.5} height={size}>
      <rect width="60" height="40" fill="#012169" />
      {/* white diagonals */}
      <path d="M0,0 L60,40 M60,0 L0,40" stroke="#FFFFFF" strokeWidth="8" />
      {/* red diagonals */}
      <path d="M0,0 L60,40 M60,0 L0,40" stroke="#C8102E" strokeWidth="3" />
      {/* white cross */}
      <path d="M30,0 V40 M0,20 H60" stroke="#FFFFFF" strokeWidth="10" />
      {/* red cross */}
      <path d="M30,0 V40 M0,20 H60" stroke="#C8102E" strokeWidth="6" />
    </Frame>
  );
}

function DKFlag({ size = 16 }) {
  return (
    <Frame width={size * 1.5} height={size}>
      <rect width="60" height="40" fill="#C8102E" />
      {/* Nordic cross */}
      <rect x="18" y="0" width="6" height="40" fill="#FFFFFF" />
      <rect x="0" y="17" width="60" height="6" fill="#FFFFFF" />
    </Frame>
  );
}

function MAFlag({ size = 16 }) {
  // Red with green pentagram
  return (
    <Frame width={size * 1.5} height={size}>
      <rect width="60" height="40" fill="#C1272D" />
      <polygon
        points="30,12 32.65,20.16 41.22,20.16 34.29,25.18 36.94,33.34 30,28.32 23.06,33.34 25.71,25.18 18.78,20.16 27.35,20.16"
        fill="none"
        stroke="#006233"
        strokeWidth="1.5"
        strokeLinejoin="miter"
      />
    </Frame>
  );
}

export function FlagIcon({ country, size = 16 }) {
  switch (country) {
    case "us":
    case "US":
      return <USFlag size={size} />;
    case "uk":
    case "gb":
    case "UK":
    case "GB":
      return <UKFlag size={size} />;
    case "dk":
    case "DK":
      return <DKFlag size={size} />;
    case "ma":
    case "MA":
      return <MAFlag size={size} />;
    default:
      return null;
  }
}

// Canonical phone list, ordered Americas → Europe → MENA.
export const ALCHAMIS_PHONES = [
  { country: "us", label: "USA", display: "+1 (973) 441-0708", tel: "+19734410708" },
  { country: "us", label: "USA", display: "+1 (908) 291-2217", tel: "+19082912217" },
  { country: "uk", label: "United Kingdom", display: "+44 7552 233929", tel: "+447552233929" },
  { country: "dk", label: "Denmark", display: "+45 31 32 10 29", tel: "+4531321029" },
  { country: "ma", label: "Morocco", display: "+212 663 179 040", tel: "+212663179040" },
];

export const ALCHAMIS_PRIMARY_EMAIL = "admin@alchamisgroup.com";
export const ALCHAMIS_CONTACT_EMAIL = "contact@alchamisgroup.com";

export default FlagIcon;
